import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Copy, Eraser, AlertTriangle } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  type Base,
  type ArithOp,
  type BitOp,
  type BitWidth,
  BASE_NAMES,
  BIT_WIDTHS,
  arithmetic,
  binaryAdditionSteps,
  binaryToDecimalSteps,
  bitwise,
  decimalToBinarySteps,
  formatBig,
  isValidForBase,
  parseBig,
  sanitizeForBase,
  toAllBases,
} from "@/lib/calculators/binary";

type Bilingual = { en: string; bn: string };
type Tab = "convert" | "arith" | "bit";

const STR = {
  h1: { en: "Binary Calculator", bn: "বাইনারি ক্যালকুলেটর" },
  desc: {
    en: "Convert between binary, octal, decimal, and hexadecimal. Perform binary arithmetic and bitwise operations with step-by-step explanations.",
    bn: "বাইনারি, অক্টাল, ডেসিমেল ও হেক্সাডেসিমেল রূপান্তর। বাইনারি গণিত ও বিটওয়াইজ অপারেশন — ধাপে ধাপে।",
  },
  tabConvert: { en: "Base Converter", bn: "বেস রূপান্তর" },
  tabArith: { en: "Binary Arithmetic", bn: "বাইনারি গণিত" },
  tabBit: { en: "Bitwise Ops", bn: "বিটওয়াইজ" },
  inputValue: { en: "Value", bn: "মান" },
  inputBase: { en: "Input base", bn: "ইনপুট বেস" },
  operandA: { en: "Operand A", bn: "অপারেন্ড A" },
  operandB: { en: "Operand B", bn: "অপারেন্ড B" },
  operation: { en: "Operation", bn: "অপারেশন" },
  shiftBy: { en: "Shift by", bn: "শিফট পরিমাণ" },
  width: { en: "Bit width", bn: "বিট প্রস্থ" },
  result: { en: "Result", bn: "ফলাফল" },
  showMethod: { en: "Show conversion method", bn: "রূপান্তর পদ্ধতি দেখান" },
  steps: { en: "Steps", bn: "ধাপ" },
  clear: { en: "Clear", bn: "মুছুন" },
  copy: { en: "Copy", bn: "কপি" },
  invalid: {
    en: "Invalid input for the selected base.",
    bn: "নির্বাচিত বেসের জন্য ইনপুট সঠিক নয়।",
  },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How to convert decimal to binary?", bn: "ডেসিমেল থেকে বাইনারি কীভাবে কনভার্ট করব?" },
    a: {
      en: "Repeatedly divide the decimal number by 2 and record the remainder each time. Read the remainders bottom-up to get the binary representation. For example, 13 ÷ 2 = 6 r1, 6 ÷ 2 = 3 r0, 3 ÷ 2 = 1 r1, 1 ÷ 2 = 0 r1 → 1101.",
      bn: "ডেসিমেল সংখ্যাটিকে বারবার ২ দিয়ে ভাগ করুন এবং প্রতিবার ভাগশেষ লিখুন। নিচ থেকে উপরে পড়লেই বাইনারি রূপ। যেমন: ১৩ → ১১০১।",
    },
  },
  {
    q: { en: "What is bitwise AND?", bn: "বিটওয়াইজ AND কী?" },
    a: {
      en: "Bitwise AND compares two binary numbers bit by bit. The result bit is 1 only if BOTH input bits are 1, otherwise 0. Used for masking and checking specific bits.",
      bn: "বিটওয়াইজ AND দুইটি বাইনারি সংখ্যার প্রতিটি বিট তুলনা করে — যদি উভয় বিট ১ হয় তবেই ফলাফল ১, নাহলে ০। মাস্কিং ও বিট চেকে ব্যবহৃত হয়।",
    },
  },
  {
    q: { en: "How does binary work?", bn: "বাইনারি সংখ্যা কীভাবে কাজ করে?" },
    a: {
      en: "Binary is base-2: every digit is either 0 or 1. Each position represents a power of 2. The rightmost is 2⁰ = 1, then 2¹ = 2, 2² = 4, and so on. To find the decimal value, sum the powers of 2 where a 1 appears.",
      bn: "বাইনারি হল বেস-২ পদ্ধতি — প্রতিটি অঙ্ক ০ অথবা ১। প্রতিটি অবস্থান ২ এর একটি ঘাত নির্দেশ করে: ডানদিক থেকে ২⁰, ২¹, ২², …। যেখানে ১ আছে সেই ঘাতগুলো যোগ করলে দশমিক মান পাওয়া যায়।",
    },
  },
  {
    q: { en: "What is two's complement?", bn: "টু'স কমপ্লিমেন্ট কী?" },
    a: {
      en: "Two's complement is the standard way computers store negative integers. To negate a binary number, invert all bits and add 1. For 8-bit, −1 = 11111111, −5 = 11111011. The leftmost bit acts as the sign bit.",
      bn: "ঋণাত্মক সংখ্যা সংরক্ষণের প্রচলিত উপায় টু'স কমপ্লিমেন্ট। সংখ্যার সকল বিট উল্টিয়ে ১ যোগ করতে হয়। বামদিকের বিট চিহ্ন বিট হিসেবে কাজ করে।",
    },
  },
  {
    q: { en: "What is hexadecimal used for?", bn: "হেক্সাডেসিমেল কোথায় ব্যবহার হয়?" },
    a: {
      en: "Hexadecimal (base-16) compactly represents binary: each hex digit maps to exactly 4 bits. Used in memory addresses, color codes (#FF5733), MAC addresses, and low-level debugging.",
      bn: "হেক্সাডেসিমেল (বেস-১৬) বাইনারিকে সংক্ষেপে প্রকাশ করে — প্রতিটি হেক্স অঙ্ক ৪ বিট। মেমরি ঠিকানা, রঙের কোড (#FF5733), MAC ঠিকানা ইত্যাদিতে ব্যবহৃত।",
    },
  },
  {
    q: { en: "How is binary addition done?", bn: "বাইনারি যোগ কীভাবে করব?" },
    a: {
      en: "Add column by column from right to left. Rules: 0+0=0, 0+1=1, 1+0=1, 1+1=10 (write 0, carry 1), 1+1+1=11 (write 1, carry 1). Same idea as decimal long addition.",
      bn: "ডান থেকে বাম দিকে কলাম অনুযায়ী যোগ করুন। নিয়ম: ০+০=০, ০+১=১, ১+১=১০ (০ লিখে ১ ক্যারি), ১+১+১=১১। দশমিক যোগের মতই।",
    },
  },
  {
    q: { en: "Why do programmers use bitwise operations?", bn: "প্রোগ্রামাররা বিটওয়াইজ কেন ব্যবহার করেন?" },
    a: {
      en: "Bitwise operations are extremely fast and let you pack flags, manipulate masks, optimize math (x << 1 = x×2), and work with hardware/network protocols where individual bits matter.",
      bn: "বিটওয়াইজ অপারেশন অত্যন্ত দ্রুত এবং ফ্ল্যাগ প্যাক, মাস্ক, দ্রুত গণিত (x << 1 = x×2) ও হার্ডওয়্যার/নেটওয়ার্ক প্রোটোকলে ব্যবহৃত হয়।",
    },
  },
  {
    q: { en: "ডেসিমেল থেকে বাইনারি কনভার্শন কীভাবে?", bn: "ডেসিমেল থেকে বাইনারি কনভার্শন কীভাবে?" },
    a: {
      en: "ভাগ পদ্ধতি ব্যবহার করুন: সংখ্যাটিকে বারবার ২ দিয়ে ভাগ করুন এবং প্রতি ধাপের ভাগশেষ লিখুন। শেষে নিচ থেকে উপরে পড়ুন — এটাই বাইনারি রূপ।",
      bn: "ভাগ পদ্ধতি ব্যবহার করুন: সংখ্যাটিকে বারবার ২ দিয়ে ভাগ করুন এবং প্রতি ধাপের ভাগশেষ লিখুন। শেষে নিচ থেকে উপরে পড়ুন — এটাই বাইনারি রূপ।",
    },
  },
];

export const Route = createFileRoute("/binary-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Binary Calculator — Binary, Hex, Octal Convert",
      description:
        "Free binary calculator. Convert between binary, decimal, octal, hex. Binary arithmetic and bitwise operations. For ICT and CSE students. বাইনারি ক্যালকুলেটর।",
      path: "/binary-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Binary Calculator", path: "/binary-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: BinaryCalculatorPage,
});

const BASES: Base[] = [2, 8, 10, 16];

function BinaryCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const [tab, setTab] = useState<Tab>("convert");

  return (
    <CalculatorLayout
      slug="binary-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Binary"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-4 flex flex-wrap gap-1.5 rounded-lg border border-border bg-muted/30 p-1">
        {(
          [
            { v: "convert", label: tr("tabConvert") },
            { v: "arith", label: tr("tabArith") },
            { v: "bit", label: tr("tabBit") },
          ] as { v: Tab; label: string }[]
        ).map((opt) => {
          const active = tab === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              onClick={() => setTab(opt.v)}
              className={cn(
                "flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition sm:text-sm",
                active
                  ? "bg-accent text-accent-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
                lang === "bn" && "font-bn",
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {tab === "convert" && <ConverterPanel />}
      {tab === "arith" && <ArithmeticPanel />}
      {tab === "bit" && <BitwisePanel />}

      <EducationalContent />
    </CalculatorLayout>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: Converter
// ---------------------------------------------------------------------------
function ConverterPanel() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const [base, setBase] = useState<Base>(10);
  const [value, setValue] = useState("13");
  const [showMethod, setShowMethod] = useState(false);

  const valid = isValidForBase(value, base);
  const parsed = useMemo(() => {
    if (!valid) return null;
    try { return parseBig(value, base); } catch { return null; }
  }, [value, base, valid]);

  const all = parsed !== null ? toAllBases(parsed) : null;

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
        <div>
          <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("inputValue")}
          </label>
          <input
            type="text"
            inputMode="text"
            spellCheck={false}
            value={value}
            onChange={(e) => setValue(sanitizeForBase(e.target.value, base))}
            className={cn(
              "h-11 w-full rounded-md border bg-card px-3 font-mono text-base outline-none focus:ring-2 focus:ring-accent/60",
              valid ? "border-border" : "border-destructive",
            )}
          />
        </div>
        <div>
          <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("inputBase")}
          </label>
          <BaseSelect value={base} onChange={(b) => { setBase(b); setValue((v) => sanitizeForBase(v, b)); }} />
        </div>
      </div>

      {!valid && value.trim() !== "" && (
        <div className={cn("flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive", lang === "bn" && "font-bn")}>
          <AlertTriangle size={14} /> {tr("invalid")}
        </div>
      )}

      {all && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-border bg-card">
          <div className="border-b border-border px-4 py-2 text-sm font-semibold">{tr("result")}</div>
          <table className="w-full text-sm">
            <tbody>
              {BASES.map((b) => (
                <tr key={b} className="border-b border-border last:border-b-0">
                  <td className={cn("w-32 px-4 py-2 text-muted-foreground", lang === "bn" && "font-bn")}>
                    {BASE_NAMES[b][lang === "bn" ? "bn" : "en"]} <span className="text-xs">(base {b})</span>
                  </td>
                  <td className="px-4 py-2 font-mono text-base font-semibold break-all">{all[b]}</td>
                  <td className="w-12 px-2 py-2 text-right">
                    <CopyBtn text={all[b]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={showMethod} onChange={(e) => setShowMethod(e.target.checked)} className="accent-accent" />
          <span className={lang === "bn" ? "font-bn" : ""}>{tr("showMethod")}</span>
        </label>
      </div>

      {showMethod && parsed !== null && (
        <ConversionMethodPanel value={parsed} fromBase={base} />
      )}
    </section>
  );
}

function ConversionMethodPanel({ value, fromBase }: { value: bigint; fromBase: Base }) {
  const lang = useAppStore((s) => s.lang);
  const abs = value < 0n ? -value : value;

  return (
    <div className="space-y-4 rounded-xl border border-border bg-background/50 p-4 text-sm">
      {fromBase !== 2 && (
        <div>
          <h4 className={cn("mb-2 font-semibold", lang === "bn" && "font-bn")}>
            {lang === "bn" ? `ডেসিমেল ${abs} → বাইনারি (বারবার ২ দিয়ে ভাগ)` : `Decimal ${abs} → Binary (divide by 2 repeatedly)`}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr><th className="px-2 py-1 text-left">÷ 2</th><th className="px-2 py-1 text-left">=</th><th className="px-2 py-1 text-left">remainder</th></tr>
              </thead>
              <tbody className="font-mono">
                {decimalToBinarySteps(abs, 2).map((s, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1">{s.dividend.toString()}</td>
                    <td className="px-2 py-1">{s.quotient.toString()}</td>
                    <td className="px-2 py-1 font-semibold text-accent">{s.remainder.toString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {lang === "bn"
              ? `নিচ থেকে উপরে ভাগশেষ পড়ুন → ${formatBig(value, 2)}`
              : `Read remainders bottom-up → ${formatBig(value, 2)}`}
          </p>
        </div>
      )}

      {fromBase === 2 && (
        <div>
          <h4 className={cn("mb-2 font-semibold", lang === "bn" && "font-bn")}>
            {lang === "bn" ? "বাইনারি → ডেসিমেল (অবস্থানগত মান)" : "Binary → Decimal (positional value)"}
          </h4>
          <div className="overflow-x-auto rounded-md border border-border bg-card p-2 font-mono text-xs">
            {binaryToDecimalSteps(formatBig(value, 2)).tex}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Arithmetic
// ---------------------------------------------------------------------------
const ARITH_OPS: { v: ArithOp; label: string }[] = [
  { v: "+", label: "+" },
  { v: "-", label: "−" },
  { v: "*", label: "×" },
  { v: "/", label: "÷" },
];

function ArithmeticPanel() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const [aStr, setAStr] = useState("1011");
  const [aBase, setABase] = useState<Base>(2);
  const [bStr, setBStr] = useState("110");
  const [bBase, setBBase] = useState<Base>(2);
  const [op, setOp] = useState<ArithOp>("+");

  const aValid = isValidForBase(aStr, aBase);
  const bValid = isValidForBase(bStr, bBase);

  const result = useMemo(() => {
    if (!aValid || !bValid) return null;
    try {
      const a = parseBig(aStr, aBase);
      const b = parseBig(bStr, bBase);
      return arithmetic(a, op, b);
    } catch {
      return null;
    }
  }, [aStr, aBase, bStr, bBase, op, aValid, bValid]);

  const additionSteps = useMemo(() => {
    if (!result || !result.ok || op !== "+") return [];
    try {
      const a = parseBig(aStr, aBase);
      const b = parseBig(bStr, bBase);
      return binaryAdditionSteps(a, b);
    } catch { return []; }
  }, [result, aStr, aBase, bStr, bBase, op]);

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr]">
        <OperandInput label={tr("operandA")} value={aStr} setValue={setAStr} base={aBase} setBase={setABase} valid={aValid} />
        <div className="flex items-end justify-center">
          <select
            value={op}
            onChange={(e) => setOp(e.target.value as ArithOp)}
            className="h-11 rounded-md border border-border bg-card px-3 font-mono text-base"
          >
            {ARITH_OPS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
        <OperandInput label={tr("operandB")} value={bStr} setValue={setBStr} base={bBase} setBase={setBBase} valid={bValid} />
      </div>

      {result && !result.ok && (
        <div className={cn("flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive", lang === "bn" && "font-bn")}>
          <AlertTriangle size={14} /> {result.error[lang]}
        </div>
      )}

      {result && result.ok && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <div className="rounded-xl border border-accent/40 bg-accent/5 p-4">
            <div className={cn("mb-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("result")}</div>
            <div className="font-mono text-lg font-semibold break-all">
              {aStr}<sub>{aBase}</sub> {ARITH_OPS.find((o) => o.v === op)?.label} {bStr}<sub>{bBase}</sub> = {result.bases[2]}<sub>2</sub>
              {" "}<span className="text-muted-foreground">(= {result.bases[10]}<sub>10</sub>)</span>
            </div>
            {op === "/" && result.remainder !== undefined && result.remainder !== 0n && (
              <div className="mt-1 text-xs text-muted-foreground">
                remainder = {formatBig(result.remainder, 2)}<sub>2</sub> ({result.remainder.toString()}<sub>10</sub>)
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <tbody>
                {BASES.map((b) => (
                  <tr key={b} className="border-b border-border last:border-b-0">
                    <td className={cn("w-32 px-4 py-2 text-muted-foreground", lang === "bn" && "font-bn")}>
                      {BASE_NAMES[b][lang === "bn" ? "bn" : "en"]}
                    </td>
                    <td className="px-4 py-2 font-mono break-all">{result.bases[b]}</td>
                    <td className="w-12 px-2 py-2 text-right"><CopyBtn text={result.bases[b]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {additionSteps.length > 0 && (
            <div className="rounded-xl border border-border bg-background/50 p-4">
              <h4 className={cn("mb-2 text-sm font-semibold", lang === "bn" && "font-bn")}>
                {lang === "bn" ? "বাইনারি যোগ — কলাম অনুযায়ী" : "Binary addition — column by column"}
              </h4>
              <pre className="overflow-x-auto whitespace-pre font-mono text-xs leading-5">{additionSteps.join("\n")}</pre>
            </div>
          )}
        </motion.div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: Bitwise
// ---------------------------------------------------------------------------
const BIT_OPS: { v: BitOp; label: string; needsB: boolean; isShift: boolean }[] = [
  { v: "AND", label: "AND (&)", needsB: true, isShift: false },
  { v: "OR", label: "OR (|)", needsB: true, isShift: false },
  { v: "XOR", label: "XOR (^)", needsB: true, isShift: false },
  { v: "NOT", label: "NOT (~)", needsB: false, isShift: false },
  { v: "SHL", label: "<< (left shift)", needsB: true, isShift: true },
  { v: "SHR", label: ">> (right shift)", needsB: true, isShift: true },
];

function BitwisePanel() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const [aStr, setAStr] = useState("1011");
  const [aBase, setABase] = useState<Base>(2);
  const [bStr, setBStr] = useState("1100");
  const [bBase, setBBase] = useState<Base>(2);
  const [op, setOp] = useState<BitOp>("AND");
  const [width, setWidth] = useState<BitWidth>(8);

  const opMeta = BIT_OPS.find((o) => o.v === op)!;
  const aValid = isValidForBase(aStr, aBase);
  const bValid = !opMeta.needsB || isValidForBase(bStr, opMeta.isShift ? 10 : bBase);

  const result = useMemo(() => {
    if (!aValid || !bValid) return null;
    try {
      const a = parseBig(aStr, aBase);
      const b = opMeta.needsB
        ? (opMeta.isShift ? BigInt(parseInt(bStr, 10) || 0) : parseBig(bStr, bBase))
        : 0n;
      return bitwise(a, op, b, width);
    } catch {
      return null;
    }
  }, [aStr, aBase, bStr, bBase, op, width, aValid, bValid, opMeta]);

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-1 gap-3">
        <OperandInput label={tr("operandA")} value={aStr} setValue={setAStr} base={aBase} setBase={setABase} valid={aValid} />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_auto] sm:items-end">
          <div>
            <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("operation")}
            </label>
            <select
              value={op}
              onChange={(e) => setOp(e.target.value as BitOp)}
              className="h-11 rounded-md border border-border bg-card px-3 text-sm"
            >
              {BIT_OPS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("width")}
            </label>
            <select
              value={width}
              onChange={(e) => setWidth(Number(e.target.value) as BitWidth)}
              className="h-11 rounded-md border border-border bg-card px-3 text-sm"
            >
              {BIT_WIDTHS.map((w) => <option key={w} value={w}>{w}-bit</option>)}
            </select>
          </div>
        </div>

        {opMeta.needsB && !opMeta.isShift && (
          <OperandInput label={tr("operandB")} value={bStr} setValue={setBStr} base={bBase} setBase={setBBase} valid={bValid} />
        )}
        {opMeta.isShift && (
          <div>
            <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
              {tr("shiftBy")}
            </label>
            <input
              type="number"
              min={0}
              max={width - 1}
              value={bStr}
              onChange={(e) => setBStr(e.target.value.replace(/[^0-9]/g, ""))}
              className="h-11 w-full rounded-md border border-border bg-card px-3 font-mono text-base outline-none focus:ring-2 focus:ring-accent/60"
            />
          </div>
        )}
      </div>

      {result && !result.ok && (
        <div className={cn("flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive", lang === "bn" && "font-bn")}>
          <AlertTriangle size={14} /> {result.error[lang]}
        </div>
      )}

      {result && result.ok && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <div className="rounded-xl border border-accent/40 bg-accent/5 p-4">
            <div className={cn("mb-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("result")}</div>
            <div className="font-mono text-base font-semibold break-all">
              {result.resultBin}<sub>2</sub> = {result.bases[10]}<sub>10</sub>
            </div>
          </div>

          <BitVisualization result={result} />

          <div className="rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <tbody>
                {BASES.map((b) => (
                  <tr key={b} className="border-b border-border last:border-b-0">
                    <td className={cn("w-32 px-4 py-2 text-muted-foreground", lang === "bn" && "font-bn")}>
                      {BASE_NAMES[b][lang === "bn" ? "bn" : "en"]}
                    </td>
                    <td className="px-4 py-2 font-mono break-all">{result.bases[b]}</td>
                    <td className="w-12 px-2 py-2 text-right"><CopyBtn text={result.bases[b]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </section>
  );
}

function BitVisualization({ result }: { result: ReturnType<typeof bitwise> & { ok: true } }) {
  const { aBin, bBin, resultBin, op, width } = result;
  const opLabel = op === "SHL" ? `<< ${result.shiftAmount}` : op === "SHR" ? `>> ${result.shiftAmount}` : op;
  const rows: { label: string; bits: string }[] = [{ label: "A", bits: aBin }];
  if (bBin) rows.push({ label: "B", bits: bBin });
  rows.push({ label: opLabel, bits: resultBin });

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-background/50 p-4">
      <table className="w-full font-mono text-xs sm:text-sm">
        <thead>
          <tr className="text-[10px] text-muted-foreground">
            <th className="px-2 py-1 text-left"></th>
            {Array.from({ length: width }).map((_, i) => (
              <th key={i} className="w-7 px-1 py-1 text-center">{width - 1 - i}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => {
            const isResult = ri === rows.length - 1;
            return (
              <tr key={ri} className={cn(isResult && "border-t border-border")}>
                <td className="px-2 py-1 font-semibold text-muted-foreground">{r.label}:</td>
                {r.bits.split("").map((bit, i) => (
                  <td
                    key={i}
                    className={cn(
                      "border border-border/50 text-center font-bold",
                      isResult && bit === "1" && "bg-accent/20 text-accent",
                      !isResult && bit === "1" && "text-foreground",
                      bit === "0" && "text-muted-foreground/60",
                    )}
                  >
                    {bit}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared UI bits
// ---------------------------------------------------------------------------
function BaseSelect({ value, onChange }: { value: Base; onChange: (b: Base) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value) as Base)}
      className="h-11 w-full rounded-md border border-border bg-card px-3 text-sm sm:w-44"
    >
      {BASES.map((b) => (
        <option key={b} value={b}>{BASE_NAMES[b].en} (base {b})</option>
      ))}
    </select>
  );
}

function OperandInput({
  label, value, setValue, base, setBase, valid,
}: {
  label: string; value: string; setValue: (s: string) => void;
  base: Base; setBase: (b: Base) => void; valid: boolean;
}) {
  const lang = useAppStore((s) => s.lang);
  return (
    <div>
      <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          spellCheck={false}
          value={value}
          onChange={(e) => setValue(sanitizeForBase(e.target.value, base))}
          className={cn(
            "h-11 flex-1 rounded-md border bg-card px-3 font-mono text-base outline-none focus:ring-2 focus:ring-accent/60",
            valid ? "border-border" : "border-destructive",
          )}
        />
        <select
          value={base}
          onChange={(e) => { const nb = Number(e.target.value) as Base; setBase(nb); setValue(sanitizeForBase(value, nb)); }}
          className="h-11 rounded-md border border-border bg-card px-2 text-xs"
        >
          {BASES.map((b) => <option key={b} value={b}>{BASE_NAMES[b].short}</option>)}
        </select>
      </div>
    </div>
  );
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = () => {
    if (typeof navigator === "undefined") return;
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <Button variant="ghost" size="sm" onClick={onClick} className="h-7 w-7 p-0">
      <Copy size={12} className={copied ? "text-accent" : ""} />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Educational content sections
// ---------------------------------------------------------------------------
function EducationalContent() {
  const lang = useAppStore((s) => s.lang);
  const en = lang === "en";
  return (
    <div className="mt-10 space-y-8">
      <Section title={en ? "Number systems explained" : "সংখ্যা পদ্ধতি"}>
        <p>
          {en
            ? "A number system is the way we write numbers. The base (or radix) tells you how many distinct digits are used and what each position is worth."
            : "সংখ্যা পদ্ধতি হল সংখ্যা লেখার নিয়ম। বেস (বা রেডিক্স) বলে দেয় কতগুলো অঙ্ক ব্যবহৃত হয় এবং প্রতিটি অবস্থানের মান কত।"}
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li><b>Binary (base 2)</b> — {en ? "digits 0, 1. Used by all digital computers." : "অঙ্ক ০, ১। কম্পিউটারের ভিত্তি।"}</li>
          <li><b>Octal (base 8)</b> — {en ? "digits 0–7. Compact 3-bit grouping." : "অঙ্ক ০–৭। ৩ বিটের গ্রুপ।"}</li>
          <li><b>Decimal (base 10)</b> — {en ? "digits 0–9. Everyday human counting." : "অঙ্ক ০–৯। দৈনন্দিন গণনা।"}</li>
          <li><b>Hexadecimal (base 16)</b> — {en ? "digits 0–9 and A–F. Compact 4-bit grouping for memory and colors." : "অঙ্ক ০–৯ ও A–F। ৪ বিটের কম্প্যাক্ট রূপ।"}</li>
        </ul>
      </Section>

      <Section title={en ? "Binary → Decimal" : "বাইনারি → ডেসিমেল"}>
        <p>
          {en
            ? "Multiply each binary digit by the corresponding power of 2 and sum. For 1011₂:"
            : "প্রতিটি বাইনারি অঙ্ককে ২ এর সংশ্লিষ্ট ঘাত দিয়ে গুণ করে যোগ করুন। 1011₂ এর জন্য:"}
        </p>
        <div className="rounded-md border border-border bg-card p-3 font-mono text-sm">
          1·2³ + 0·2² + 1·2¹ + 1·2⁰ = 8 + 0 + 2 + 1 = 11
        </div>
      </Section>

      <Section title={en ? "Decimal → Binary (division method)" : "ডেসিমেল → বাইনারি (ভাগ পদ্ধতি)"}>
        <p>
          {en
            ? "Divide by 2 repeatedly and write down each remainder. Read the remainders from bottom to top — that is the binary number. Example: 13 → 1101."
            : "বারবার ২ দিয়ে ভাগ করে প্রতিবার ভাগশেষ লিখুন। নিচ থেকে উপরে পড়লেই বাইনারি রূপ। উদাহরণ: ১৩ → ১১০১।"}
        </p>
        <pre className="overflow-x-auto rounded-md border border-border bg-card p-3 font-mono text-xs leading-5">
{`13 ÷ 2 = 6  remainder 1
 6 ÷ 2 = 3  remainder 0
 3 ÷ 2 = 1  remainder 1
 1 ÷ 2 = 0  remainder 1
                    ↑ read up: 1101`}
        </pre>
      </Section>

      <Section title={en ? "Binary arithmetic" : "বাইনারি গণিত"}>
        <p>
          {en
            ? "Addition, subtraction and multiplication work just like in decimal — column by column, with carries (addition) or borrows (subtraction)."
            : "যোগ, বিয়োগ ও গুণ দশমিকের মতই কলাম অনুযায়ী হয় — ক্যারি বা ধার সহ।"}
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li>0 + 0 = 0, 0 + 1 = 1, 1 + 1 = 10 (carry 1), 1 + 1 + 1 = 11</li>
          <li>1 × 1 = 1, anything × 0 = 0</li>
        </ul>
      </Section>

      <Section title={en ? "Two's complement (negatives)" : "টু'স কমপ্লিমেন্ট (ঋণাত্মক সংখ্যা)"}>
        <p>
          {en
            ? "Computers store negative integers in two's complement: invert all bits of the positive value and add 1. The leftmost bit is the sign bit (1 = negative). In 8-bit, −5 is 11111011."
            : "কম্পিউটারে ঋণাত্মক পূর্ণসংখ্যা টু'স কমপ্লিমেন্টে সংরক্ষিত হয়: ধনাত্মক রূপের সকল বিট উল্টিয়ে ১ যোগ করতে হয়। বামদিকের বিট চিহ্ন বিট। ৮ বিটে −৫ = 11111011।"}
        </p>
      </Section>

      <Section title={en ? "Bitwise operations" : "বিটওয়াইজ অপারেশন"}>
        <ul className="ml-5 list-disc space-y-1">
          <li><b>AND (&)</b> — {en ? "1 if both bits are 1." : "উভয় বিট ১ হলে ১।"}</li>
          <li><b>OR (|)</b> — {en ? "1 if at least one bit is 1." : "অন্তত একটি বিট ১ হলে ১।"}</li>
          <li><b>XOR (^)</b> — {en ? "1 if the bits differ." : "বিট ভিন্ন হলে ১।"}</li>
          <li><b>NOT (~)</b> — {en ? "Invert all bits." : "সকল বিট উল্টে যায়।"}</li>
          <li><b>{"<<"} / {">>"}</b> — {en ? "Shift bits left/right. Each left shift = ×2, right shift = ÷2 (integer)." : "বিট বাম/ডানে সরায়। বাম শিফট = ×২, ডান শিফট = ÷২।"}</li>
        </ul>
      </Section>

      <Section title={en ? "For ICT students (SSC / HSC)" : "ICT শিক্ষার্থীদের জন্য (SSC / HSC)"}>
        <p>
          {en
            ? "Common syllabus topics: number system conversion (decimal ↔ binary ↔ octal ↔ hex), binary addition and subtraction, 1's and 2's complement, BCD code, ASCII, basic logic gates (AND, OR, NOT) and Boolean algebra."
            : "সিলেবাসের সাধারণ অধ্যায়: সংখ্যা পদ্ধতি রূপান্তর (ডেসিমেল ↔ বাইনারি ↔ অক্টাল ↔ হেক্স), বাইনারি যোগ-বিয়োগ, ১'স ও ২'স কমপ্লিমেন্ট, BCD কোড, ASCII, মৌলিক লজিক গেট ও বুলিয়ান বীজগণিত।"}
        </p>
      </Section>

      <Section title={en ? "For CSE students" : "CSE শিক্ষার্থীদের জন্য"}>
        <p>
          {en
            ? "Bitwise operations are everywhere in low-level code: bit flags and packed structs, bitmasks (x & 0xFF), efficient multiplication/division by powers of 2, hashing, network protocols (subnet masks), graphics (color channels), and competitive programming tricks."
            : "নিম্নস্তরের কোডে বিটওয়াইজ অপারেশন সর্বত্র: বিট ফ্ল্যাগ, প্যাকড স্ট্রাক্ট, মাস্কিং (x & 0xFF), দ্রুত গুণ-ভাগ, হ্যাশিং, নেটওয়ার্ক প্রোটোকল (সাবনেট মাস্ক), গ্রাফিক্স ও প্রতিযোগিতামূলক প্রোগ্রামিং।"}
        </p>
      </Section>

      <Section title={en ? "ASCII / UTF-8 quick reference" : "ASCII / UTF-8 সংক্ষিপ্ত রেফারেন্স"}>
        <p>
          {en
            ? "ASCII assigns a 7-bit code (0–127) to common characters. 'A' = 65, 'a' = 97, '0' = 48, space = 32. UTF-8 extends ASCII: 1-byte for ASCII, 2–4 bytes for other code points (including Bengali in U+0980–U+09FF)."
            : "ASCII সাধারণ অক্ষরকে ৭ বিট কোড (০–১২৭) দেয়। 'A' = ৬৫, 'a' = ৯৭, '0' = ৪৮, স্পেস = ৩২। UTF-8 ASCII কে বাড়িয়ে অন্যান্য কোড পয়েন্টের জন্য ২–৪ বাইট ব্যবহার করে (বাংলা U+0980–U+09FF)।"}
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const lang = useAppStore((s) => s.lang);
  return (
    <section className={cn(lang === "bn" && "font-bn")}>
      <h2 className="mb-2 text-lg font-bold">{title}</h2>
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}

// Suppress unused warnings for utility imports kept for future extension.
void Eraser;
